import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Helmet} from "react-helmet";
import {Spinner} from "../../../shared";
import {getLocalDateString, getSerieDates, isFutureDate} from "../../../../utils/utils";
import TmdbImage from "../../../shared/tmdb-image/tmdb-image";
import TmdbScore from "../../tmdbScore/tmdbScore";
import TmdbCollectionSelector from "../../movies/tmdbCollectionSelector/tmdbCollectionSelector";
import TmdbViewLater from "../../movies/tmdbViewLater/TmdbViewLater";
import TmdbLikeItem from "../../movies/tmdbLikeItem/tmdb-like-item";
import TmdbNotepad from "../../movies/tmdbNotepad/tmdbNotepad";
import TmdbItemSuggestions from "../../movies/tmdb-item-suggestions/tmdb-item-suggestions";
import TmdbItemReviews from "../../movies/tmdb-item-reviews/tmdb-item-reviews";
import SpoilersPreferenceModal from "../../movies/spoilers-preference-modal/spoilers-preference-modal";
import UseUserPreferences from "../../../user-app-config/hooks/use-user-preferences";
import useSerieDetailsData from "../../hooks/useSerieDetailsData";
import useSeasonEpisodesData from "../../hooks/useSeasonEpisodesData";
import TvMediaCard from "../components/TvMediaCard";
import TvModal from "../components/TvModal";

type Props = {
    squadflixTvHook?: any;
};

const SquadflixTvSerieDetails: React.FC<Props> = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {userPreferences, loading: loadingPreferences} = UseUserPreferences();
    const [activeTab, setActiveTab] = useState<"episodes" | "suggestions" | "reviews" | "details">("episodes");
    const [selectedEpisodeForModal, setSelectedEpisodeForModal] = useState<any>(null);

    const {
        serie,
        season,
        setSeason,
        lastEpisode,
        isFirstEpisode,
        logoImage,
        backgroundImage,
        from,
        handleClickPlay,
        clearAndGoBack
    } = useSerieDetailsData(id!);

    const {
        episodes,
        loading: loadingEpisodes,
        updateItemInSeason,
        markEpisodeAsView
    } = useSeasonEpisodesData(serie, season);

    useEffect(() => {
        setActiveTab("episodes");
    }, [id]);

    if (!serie) {
        return (
            <div className="tv-loading-center">
                <Spinner />
            </div>
        );
    }

    const backdropPath = backgroundImage?.file_path || serie.backdrop_path || serie.poster_path;
    const hideSpoilersGlobal =
        userPreferences.hideSpoilers || userPreferences.hideSpoilers == null || loadingPreferences;

    return (
        <div className="tv-details-screen">
            <Helmet>
                <meta charSet="utf-8" />
                <title>{`${serie.name} | SquadFlix TV`}</title>
            </Helmet>

            {/* Full-bleed 16:9 Backdrop */}
            <div className="tv-details-screen__backdrop">
                {backdropPath && (
                    <TmdbImage
                        src={backdropPath}
                        size="original"
                        className="tv-details-screen__backdrop-img"
                    />
                )}
                <div className="tv-details-screen__vignette" />
            </div>

            <div className="tv-details-screen__body">
                {/* Top Back Bar */}
                <div className="tv-details-screen__topbar" data-tv-row="tv-serie-topbar">
                    <button
                        type="button"
                        data-tv-focusable="true"
                        className="tv-btn tv-btn--glass"
                        onClick={clearAndGoBack}>
                        <i className="fa-solid fa-arrow-left pe-2"></i>
                        {from?.customKey || "Volver al catálogo"}
                    </button>
                </div>

                {/* Serie Hero Metadata */}
                <div className="tv-details-screen__hero">
                    {logoImage ? (
                        <TmdbImage
                            src={logoImage.file_path}
                            className="tv-details-screen__logo"
                        />
                    ) : (
                        <h1 className="tv-details-screen__title">{serie.name}</h1>
                    )}

                    <div className="tv-details-screen__meta-row">
                        <span className="tv-pill tv-pill--accent">Serie</span>
                        {serie.vote_average != null && (
                            <span className="tv-pill">
                                <i className="fa-solid fa-star text-warning pe-1"></i>
                                {serie.vote_average.toFixed(1)}
                            </span>
                        )}
                        <span className="tv-pill">{getSerieDates(serie)}</span>
                        <span className="tv-pill">
                            {serie.seasons?.length ?? 0}{" "}
                            {serie.number_of_seasons === 1 ? "Temporada" : "Temporadas"}
                        </span>
                        {serie.genres?.map((g: any) => (
                            <span key={g.id} className="tv-pill">
                                {g.name}
                            </span>
                        ))}
                    </div>

                    {serie.overview && (
                        <p className="tv-details-screen__overview">{serie.overview}</p>
                    )}

                    {/* TV Action Buttons */}
                    <div className="tv-details-screen__actions" data-tv-row="tv-serie-actions">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus="true"
                            className="tv-btn tv-btn--primary tv-btn--lg"
                            onClick={handleClickPlay}>
                            <i className="fa-solid fa-play pe-2"></i>
                            {isFirstEpisode || !lastEpisode
                                ? "Reproducir S1:E1"
                                : `Continuar S${lastEpisode.season_number}:E${lastEpisode.episode_number}`}
                        </button>

                        <TmdbCollectionSelector item={serie} isMovie={false} />
                        <TmdbViewLater tmdbItem={serie} />
                        <TmdbLikeItem tmdbItem={serie} />

                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-btn tv-btn--glass"
                            onClick={() => navigate("#notes")}>
                            <i className="fa-solid fa-note-sticky pe-2"></i>Mis Notas
                        </button>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="tv-details-screen__tabs-section">
                    <div className="tv-details-screen__tabs" data-tv-row="tv-serie-tabs">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "episodes" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("episodes")}>
                            <i className="fa-solid fa-film pe-2"></i>Episodios
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "suggestions" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("suggestions")}>
                            <i className="fa-solid fa-wand-magic-sparkles pe-2"></i>Sugerencias
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "reviews" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("reviews")}>
                            <i className="fa-solid fa-comments pe-2"></i>Reseñas
                        </button>
                        <button
                            type="button"
                            data-tv-focusable="true"
                            className={`tv-tab-btn ${activeTab === "details" ? "is-active" : ""}`}
                            onClick={() => setActiveTab("details")}>
                            <i className="fa-solid fa-circle-info pe-2"></i>Detalles
                        </button>
                    </div>

                    {activeTab === "episodes" && (
                        <div className="tv-serie-episodes">
                            {/* Horizontal Season Pills Selector */}
                            <div className="tv-season-pills" data-tv-row="tv-season-selector">
                                {(serie.seasons || []).map((s: any) => {
                                    const isSelected = Number(season) === Number(s.season_number);
                                    return (
                                        <button
                                            key={s.id || s.season_number}
                                            type="button"
                                            data-tv-focusable="true"
                                            className={`tv-pill-btn ${isSelected ? "is-active" : ""}`}
                                            onClick={() => setSeason(s.season_number)}>
                                            {s.season_number === 0
                                                ? "Especiales"
                                                : `Temporada ${s.season_number}`}
                                            {s.episode_count ? ` (${s.episode_count})` : ""}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Episodes Grid / Carousel */}
                            {loadingEpisodes ? (
                                <div className="tv-loading-center py-5">
                                    <Spinner />
                                </div>
                            ) : (
                                <div
                                    className="tv-episodes-grid"
                                    data-tv-row="tv-serie-episodes"
                                    data-tv-grid="true">
                                    {episodes.map((ep: any) => {
                                        const isSpoiler = hideSpoilersGlobal && !ep.view;
                                        const future = ep.air_date ? isFutureDate(new Date(ep.air_date)) : false;
                                        return (
                                            <TvMediaCard
                                                key={`${ep.episode_number}_${ep.view}`}
                                                width="100%"
                                                aspectRatio="16/9"
                                                tmdbSrc={ep.still_path || serie.backdrop_path}
                                                isSpoiler={isSpoiler}
                                                showPlayOverlay
                                                badgeTopLeft={`Episodio ${ep.episode_number}`}
                                                badgeBottomRight={
                                                    ep.view ? (
                                                        <span className="text-success">
                                                            <i className="fa-solid fa-eye pe-1"></i>Visto
                                                        </span>
                                                    ) : future ? (
                                                        "Próximamente"
                                                    ) : undefined
                                                }
                                                badgeBottomLeft={
                                                    ep.notes ? <i className="fa-solid fa-note-sticky"></i> : undefined
                                                }
                                                meta={
                                                    <>
                                                        {ep.air_date && getLocalDateString(ep.air_date)}
                                                        {ep.runtime ? ` • ${ep.runtime} min` : ""}
                                                    </>
                                                }
                                                title={
                                                    isSpoiler
                                                        ? `Episodio ${ep.episode_number}`
                                                        : `E${ep.episode_number}. ${ep.name}`
                                                }
                                                subtitle={!isSpoiler ? ep.overview : "Sinopsis oculta anti-spoilers"}
                                                onClick={() =>
                                                    navigate(
                                                        `/squadflix/series/${serie.id}/${season}/${ep.episode_number}`
                                                    )
                                                }
                                                onSecondaryAction={() => setSelectedEpisodeForModal(ep)}
                                                secondaryActionLabel="Opciones"
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === "suggestions" && (
                        <div
                            className="tv-details-screen__tab-panel"
                            data-tv-row="tv-serie-suggestions"
                            data-tv-grid="true">
                            <TmdbItemSuggestions item={serie} isMovie={false} />
                        </div>
                    )}

                    {activeTab === "reviews" && (
                        <div
                            className="tv-details-screen__tab-panel"
                            data-tv-row="tv-serie-reviews"
                            data-tv-grid="true">
                            <TmdbItemReviews item={serie} isMovie={false} />
                        </div>
                    )}

                    {activeTab === "details" && (
                        <div className="tv-details-screen__info-grid">
                            <div>
                                <h3>{serie.name}</h3>
                                <p className="text-muted">{serie.overview}</p>
                            </div>
                            <div>
                                <TmdbScore score={serie.vote_average} info={undefined} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <SpoilersPreferenceModal serie={serie} />

            {/* Episode Options TV Modal */}
            <TvModal
                open={!!selectedEpisodeForModal}
                onClose={() => setSelectedEpisodeForModal(null)}
                title={
                    selectedEpisodeForModal
                        ? `S${season}:E${selectedEpisodeForModal.episode_number} — ${
                              hideSpoilersGlobal && !selectedEpisodeForModal.view
                                  ? `Episodio ${selectedEpisodeForModal.episode_number}`
                                  : selectedEpisodeForModal.name
                          }`
                        : ""
                }
                subtitle={serie.name}>
                {selectedEpisodeForModal && (
                    <div className="tv-modal-list">
                        <button
                            type="button"
                            data-tv-focusable="true"
                            data-tv-autofocus="true"
                            className="tv-modal-option-btn"
                            onClick={() => {
                                const epNum = selectedEpisodeForModal.episode_number;
                                setSelectedEpisodeForModal(null);
                                navigate(`/squadflix/series/${serie.id}/${season}/${epNum}`);
                            }}>
                            <span>
                                <i className="fa-solid fa-play pe-3 text-danger"></i>Reproducir episodio
                            </span>
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>

                        <button
                            type="button"
                            data-tv-focusable="true"
                            className="tv-modal-option-btn"
                            onClick={() => {
                                const nextView = !selectedEpisodeForModal.view;
                                markEpisodeAsView(selectedEpisodeForModal, nextView, () => {
                                    const updated = {...selectedEpisodeForModal, view: nextView};
                                    updateItemInSeason(updated);
                                    setSelectedEpisodeForModal(updated);
                                });
                            }}>
                            <span>
                                <i
                                    className={`fa-solid ${
                                        selectedEpisodeForModal.view ? "fa-eye-slash" : "fa-eye"
                                    } pe-3`}></i>
                                {selectedEpisodeForModal.view
                                    ? "Marcar como no visto"
                                    : "Marcar como visto"}
                            </span>
                        </button>
                    </div>
                )}
            </TvModal>

            <div style={{display: "none"}}>
                <TmdbNotepad tmdbItem={serie} forceDarkMode="dark" customUrl={undefined} />
            </div>
        </div>
    );
};

export default SquadflixTvSerieDetails;
